/**
 * policyErrors — the one error shape the account-default / location-value
 * policy modules throw, carrying the backend's error code so screens can
 * branch on it. The ApiClient flattens the error body: codes live on
 * `errorBody`, never on the thrown message.
 *
 * POLICY_FIELD_LOCKED (409): a location wrote a field the account has locked.
 * The UI never sends locked fields (see buildPatchBody), so this is a race
 * with an account admin locking it meanwhile; the message says who owns it.
 */
export const POLICY_FIELD_LOCKED = 'POLICY_FIELD_LOCKED';
export const POLICY_FIELD_LOCKED_MESSAGE = 'This setting is controlled by the account.';

export class PolicyApiError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'PolicyApiError';
    this.code = code;
    this.status = status;
  }
}

export function isPolicyFieldLocked(err: unknown): boolean {
  return err instanceof PolicyApiError && err.code === POLICY_FIELD_LOCKED;
}

/** Build the error for a failed ApiClient response — the lock code gets its own wording. */
export function toPolicyApiError(res: { error?: string; errorBody?: any; status?: number }, fallback: string): PolicyApiError {
  const eb: any = res.errorBody;
  const code: string | undefined = eb?.code || eb?.error_code;
  if (code === POLICY_FIELD_LOCKED) return new PolicyApiError(POLICY_FIELD_LOCKED_MESSAGE, code, res.status);
  const message = (eb && (eb.error || eb.message)) || res.error || fallback;
  return new PolicyApiError(message, code, res.status);
}

/** Message for an inline banner: the lock wording when it is that error, else the error's own text. */
export function policyErrorMessage(err: unknown, fallback: string): string {
  if (isPolicyFieldLocked(err)) return POLICY_FIELD_LOCKED_MESSAGE;
  return err instanceof Error ? err.message : fallback;
}
